import type { LucideIcon } from "lucide-react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  /** Kept for backwards-compat with callers; DESIGN v7 forbids glyphs in tiles. Ignored. */
  icon?: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
  href?: string;
}

export function StatCard({ title, value, description, trend, className, href }: StatCardProps) {
  const cardContent = (
    <Card
      className={cn(
        "shadow-none",
        href && "hover-elevate cursor-pointer transition-colors",
        className,
      )}
    >
      <CardContent className="p-5">
        <div className="space-y-3">
          <p className="caption-mono">{title}</p>
          <div className="flex items-baseline gap-2">
            <p className="num-mono text-3xl font-semibold tracking-tight leading-none">{value}</p>
            {trend && (
              <span
                className={cn(
                  "num-mono text-xs font-semibold",
                  trend.isPositive ? "text-[hsl(var(--forest-pulse))]" : "text-primary",
                )}
              >
                {trend.isPositive ? "+" : ""}
                {trend.value}%
              </span>
            )}
          </div>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </CardContent>
    </Card>
  );

  if (href) {
    return (
      <Link href={href} data-testid={`link-stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
